// @flow

import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage } from 'react-intl';
import { Dashboard } from 'styles/dashboard';
import type {
    ReduxDispatch,
    ImmutableList,
    ImmutableMap,
    UserType,
    ColumnType,
    PaginatedType,
} from 'types';

import OrganizationListItem from 'components/OrganizationListItemMaintenance';
import DashboardDataComponent from 'components/DashboardDataComponent';
import OutlineButton from 'components/OutlineButton';
import Chip from 'components/Chip';
import colors from 'styles/colors';

import { selectLocale } from 'services/Language/selectors';

type Props = {
    columns: Array<ColumnType>,
    id: ?string,
    tableData: ?ImmutableList<string>,
    totalItemsCount: ?number,
    onClickDataItem: ?(number) => void,
    onClickViewMore: ?() => void,
    locale: string,
    totalPrice: ?number,
    component: ?ReactComponent,
    paginated: ?PaginatedType,
    loading: ?boolean,
    itemsDisplayed?: number,
    showHeaderCaret?: boolean,
    report?: boolean,
};

const DashboardComponent = (props: Props) => {
    const {
        columns,
        vehicleId,
        tableData,
        totalItemsCount,
        onClickDataItem,
        onClickViewMore,
        component,
        paginated,
        totalPrice,
        locale,
        loading,
        itemsDisplayed = 10,
        report = false,
    } = props;

    const [showViewMore, setShowViewMore] = useState(false);
    const [showUserOptions, setShowUserOptions] = useState(-1);
    const [showEquipmentOptions, setShowEquipmentOptions] = useState(-1);

    const handleOnClickDataItem = useCallback(
        (id: number) => () => {
            if (onClickDataItem) onClickDataItem(id);
        },
        [onClickDataItem]
    );

    const handleShowUserOptions = useCallback((user: UserType) => {
        setShowUserOptions((prev) => (prev === user.get('userId') ? -1 : user.get('userId')));
    }, []);

    const handleToggleChange = useCallback((e, data) => {
        setToggledIds((prev) => {
            const updatedToggledIds = new Set(prev);
            if (updatedToggledIds.has(data.get('reportId'))) {
                updatedToggledIds.delete(data.get('reportId'));
            } else {
                updatedToggledIds.add(data.get('reportId'));
            }
            return updatedToggledIds;
        });
    }, []);

    const handleShowEquipmentOptions = useCallback((equipment: EquipmentType) => {
        setShowEquipmentOptions(
            (prev) => (prev === equipment.get('id') ? -1 : equipment.get('id'))
        );
    }, []);

    const renderFooterItem = (columnSize) => {
        const averagePrice = totalPrice / totalItemsCount;
        return (
            <Dashboard.FooterContainer columns={columnSize}>
                <Dashboard.FooterItem>
                    <Dashboard.FooterText>
                        <FormattedMessage id="containers.Reports.Generators.Maintenance.maintenanceCount" />: <br />
                        {totalItemsCount}
                    </Dashboard.FooterText>
                </Dashboard.FooterItem>
                <Dashboard.FooterItem>
                    <Dashboard.FooterText>
                    <FormattedMessage id="containers.Reports.Generators.Maintenance.totalCost" />: <br />
                    {totalPrice.toFixed(2)}
                    </Dashboard.FooterText>
                </Dashboard.FooterItem>
                <Dashboard.FooterItem>
                    <Dashboard.FooterText>
                    <FormattedMessage id="containers.Reports.Generators.Maintenance.averageCost" />: <br />
                        {averagePrice.toFixed(2)}
                    </Dashboard.FooterText>
                </Dashboard.FooterItem>
            </Dashboard.FooterContainer>
        );
    };

    const renderTableData = () => {
        let dataItems = tableData;
        if (!tableData) {
            return false;
        }

        if (
            typeof onClickViewMore === 'function' &&
            !props.list &&
            dataItems &&
            dataItems.size > itemsDisplayed
        ) {
            // show the first 10 items.
            dataItems = dataItems.take(itemsDisplayed);
            setShowViewMore(true);
        }

        if (report) {
            dataItems = dataItems.take(itemsDisplayed);
        }

        if (paginated && paginated.get('currentPage') !== paginated.get('lastPage')) {
            setShowViewMore(true);
        }
        const listItems = dataItems.map((data, index) => {
            let componentProps = {
                rowSpan: props.columns.length,
                data,
            };

            if (component) {
                componentProps = {
                    rowSpan: columns.length,
                    data,
                    ...props,
                    showUserOptions: showUserOptions,
                    showEquipmentOptions: showEquipmentOptions,
                    handleShowUserOptions: handleShowUserOptions,
                    handleShowEquipmentOptions: handleShowEquipmentOptions,
                };
            }

            if (!data) {
                return false;
            }
            return (
                <Dashboard.DataItemCustom
                    columns={columns.length}
                    onClick={onClickDataItem ? handleOnClickDataItem(data.id) : null}
                >
                    {component ? (
                        <DashboardDataComponent {...componentProps} component={component} />
                    ) : (
                        <OrganizationListItem
                            vehicleId={vehicleId}
                            columns={columns}
                            data={data}
                            onToggleChange={(e) => handleToggleChange(e, data)}
                        />
                    )}
                </Dashboard.DataItemCustom>
            );
        });

        return (
            <Dashboard.GridContainerCustom columns={columns.length}>
                {listItems}
            </Dashboard.GridContainerCustom>
        );
    };
    const renderShowMoreUsers = () => {
        return tableData.size > 0 ? (
            <Dashboard.ViewMore onClick={onClickViewMore}>
                <OutlineButton outlineColor={colors.green73} hoverTextColor={colors.white}>
                    {loading ? (
                        <FormattedMessage id={'components.Loading'} />
                    ) : (
                        <FormattedMessage id={'views.BranchPage.ViewMore'} />
                    )}
                </OutlineButton>
            </Dashboard.ViewMore>
        ) : null;
    };

    let columnSize = 8;
    return (
        <Dashboard loading={loading}>
            <Dashboard.HeaderContainer columns={columnSize}>
                <Dashboard.HeaderItem key={columns[0].key}>
                    <Dashboard.HeaderText>{columns[0].label}</Dashboard.HeaderText>
                </Dashboard.HeaderItem>
                <Dashboard.HeaderItem>
                    <Dashboard.HeaderText>
                        {columns[1].label}
                        <br />
                        {columns[2].label}
                    </Dashboard.HeaderText>
                </Dashboard.HeaderItem>
                <Dashboard.HeaderItem>
                    <Dashboard.HeaderText>{columns[3].label}</Dashboard.HeaderText>
                </Dashboard.HeaderItem>
                <Dashboard.HeaderItem>
                    <Dashboard.HeaderText>
                        {columns[4].label}
                        <br />
                        {columns[5].label}
                    </Dashboard.HeaderText>
                </Dashboard.HeaderItem>
                <Dashboard.HeaderItem>
                    <Dashboard.HeaderText>
                        {columns[6].label}
                        <br />
                        {columns[7].label}
                    </Dashboard.HeaderText>
                </Dashboard.HeaderItem>
                <Dashboard.HeaderItem>
                    <Dashboard.HeaderText>
                        {columns[8].label}
                        <br />
                        {columns[9].label}
                    </Dashboard.HeaderText>
                </Dashboard.HeaderItem>
                <Dashboard.HeaderItem>
                    <Dashboard.HeaderText>
                        {columns[10].label}
                        <br />
                        {columns[11].label}
                    </Dashboard.HeaderText>
                </Dashboard.HeaderItem>
                <Dashboard.HeaderItem>
                    <Dashboard.HeaderText>{columns[12].label}</Dashboard.HeaderText>
                </Dashboard.HeaderItem>
            </Dashboard.HeaderContainer>
            <Dashboard.Split columns={columnSize} color={colors.black} height={'3px'} />
            {renderTableData()}
            <Dashboard.Split columns={columnSize} color={colors.black} height={'3px'} />
            {totalItemsCount && renderFooterItem(columnSize)}
            {showViewMore ? renderShowMoreUsers() : null}
        </Dashboard>
    );
};

const mapStateToProps = createStructuredSelector({
    locale: selectLocale(),
});

export default withRouter(connect(mapStateToProps)(DashboardComponent));
